/**
 * What a field lets through while it is being typed.
 *
 * Every one of these is a filter, not a validator: it returns the longest
 * prefix-shaped value that is still a legal thing to have typed, and the
 * caller feeds that straight back into the field. A person cannot put a
 * letter inside a phone number or a digit inside a name, because the
 * character never lands — there is no error message to read, and nothing to
 * clean up at submit time.
 *
 * Filtering is not a substitute for the checks that follow. `functions/` and
 * `firestore.rules` still refuse a malformed value, and the screens still
 * report a value that is well-formed but wrong (an age below the floor, an
 * OTP that does not match). This layer only removes the class of mistake that
 * the keyboard should never have allowed in the first place — which on the
 * web is every mistake, since an `<input>` honours none of React Native's
 * `keyboardType` hints.
 *
 * The rules are the ones the server already enforces, restated here so the
 * two agree:
 *   phone        functions/phone.js      05XXXXXXXX / +9665XXXXXXXX
 *   OTP          functions/verifyOtpAndSignIn.js   4-8 digits
 *   invite code  functions/guardians.js  6 chars of a spoken alphabet
 *   PIN          functions/parentPin.js  4 digits
 */

/** Arabic-Indic digits, which an Arabic keyboard produces by default. */
const ARABIC_INDIC = '٠١٢٣٤٥٦٧٨٩';
/** Eastern Arabic-Indic, used by Persian and Urdu keyboards. */
const EXTENDED_INDIC = '۰۱۲۳۴۵۶۷۸۹';

/** Folds any Arabic digit to its Western twin; leaves everything else alone. */
function toWesternDigits(value: string): string {
  let out = '';
  for (const ch of value) {
    const arabic = ARABIC_INDIC.indexOf(ch);
    if (arabic !== -1) { out += String(arabic); continue; }
    const extended = EXTENDED_INDIC.indexOf(ch);
    if (extended !== -1) { out += String(extended); continue; }
    out += ch;
  }
  return out;
}

/** Digits and nothing else, capped. */
function digits(value: string, max: number): string {
  return toWesternDigits(value).replace(/\D/g, '').slice(0, max);
}

/**
 * A Saudi mobile as it is being typed.
 *
 * The separators a person puts between groups are dropped rather than
 * refused, so a number pasted out of a message arrives canonical. Thirteen is
 * `+966` plus nine digits — the longest legal spelling.
 */
export function phoneInput(value: string): string {
  const plus = value.trimStart().startsWith('+');
  return (plus ? '+' : '') + digits(value, plus ? 12 : 10);
}

/** The code from an SMS: the server accepts four to eight digits. */
export function otpInput(value: string): string {
  return digits(value, 8);
}

/** A parent's PIN: exactly four digits, so four is also the ceiling. */
export function pinInput(value: string): string {
  return digits(value, 4);
}

/**
 * A guardian invite code.
 *
 * The alphabet omits I, O, 0 and 1 on purpose — these codes are read aloud
 * and written down — so those characters are dropped here too rather than
 * being typed in and refused later.
 */
const INVITE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export function inviteCodeInput(value: string): string {
  let out = '';
  for (const ch of toWesternDigits(value).toUpperCase()) {
    if (INVITE_ALPHABET.includes(ch)) out += ch;
    if (out.length === 6) break;
  }
  return out;
}

/** An age in years. Two digits covers every person this product serves. */
export function ageInput(value: string): string {
  return digits(value, 2);
}

/**
 * A person's name.
 *
 * Letters in any script, plus the three marks that appear inside real names:
 * a space, an apostrophe, a hyphen. Digits and punctuation are dropped — a
 * name is the one field where a stray character survives all the way onto a
 * printed certificate.
 *
 * Runs of spaces collapse and a leading space never lands, so a name cannot
 * be stored with invisible padding that makes two identical names unequal.
 */
const NAME_ALLOWED = /[\p{L}\p{M} '-]/u;
export function personNameInput(value: string): string {
  let out = '';
  for (const ch of value) {
    if (!NAME_ALLOWED.test(ch)) continue;
    // Arabic-Indic digits are digits, not letters — but \p{N} would also
    // catch them, and dropping them here keeps one rule for both scripts.
    if (ch === ' ' && (out === '' || out.endsWith(' '))) continue;
    out += ch;
    if (out.length === 40) break;
  }
  return out;
}

/**
 * A count the admin forms store as a number — points, an order, a limit.
 *
 * Leading zeros go, because `007` and `7` are one value and the field should
 * not suggest otherwise; a lone `0` stays, because zero is a real answer.
 */
export function wholeNumberInput(value: string, max = 9): string {
  // Zeros go BEFORE the ceiling, not after: trimming second, '0286' was cut
  // to '028' and then read as 28 — an ayah number silently became a different
  // ayah number.
  const d = digits(value, max + LEADING_ZERO_SLACK).replace(/^0+(?=\d)/, '');
  return d.slice(0, max);
}

/**
 * How many leading zeros a person may type before the ceiling starts eating
 * real digits. Enough for a pasted, zero-padded number; not unbounded, since
 * `digits` has to stop somewhere.
 */
const LEADING_ZERO_SLACK = 8;

/* ------------------------------------------------------------------------ *
 * Formats, declared on the element
 *
 * A filter alone still leaves the rule in a handler, where the next field
 * copies it or forgets it, and where the ELEMENT itself claims nothing: the
 * keyboard that opens, the ceiling the browser enforces, the autofill the
 * platform offers and the direction digits run in are all separate props a
 * call site sets by hand. Naming the format once puts all of them on the
 * field, so `format="phone"` IS the specification and there is nothing left
 * to restate.
 * ------------------------------------------------------------------------ */

export type FormatName =
  | 'phone' | 'otp' | 'pin' | 'inviteCode' | 'age' | 'personName' | 'ayah' | 'count';

export type Format = {
  /** The longest legal prefix of what was typed. */
  filter: (value: string) => string;
  /** The same ceiling the filter enforces, stated so the platform holds it too. */
  maxLength: number;
  /** React Native's keyboard. */
  keyboardType: 'phone-pad' | 'number-pad' | 'default';
  /** The web's equivalent. */
  inputMode: 'tel' | 'numeric' | 'text';
  /** What the platform may offer to fill in, when it has something to offer. */
  autoComplete?: 'tel' | 'one-time-code';
  /**
   * Whether the content reads left to right whatever the interface language
   * is. A phone number and a PIN do; a name does not.
   */
  ltr: boolean;
};

export const FORMATS: Record<FormatName, Format> = {
  phone: {
    filter: phoneInput,
    // '+966' and nine digits — the longest spelling functions/phone.js takes.
    maxLength: 13,
    keyboardType: 'phone-pad',
    inputMode: 'tel',
    autoComplete: 'tel',
    ltr: true,
  },
  otp: {
    filter: otpInput, maxLength: 8, keyboardType: 'number-pad', inputMode: 'numeric',
    autoComplete: 'one-time-code', ltr: true,
  },
  pin: { filter: pinInput, maxLength: 4, keyboardType: 'number-pad', inputMode: 'numeric', ltr: true },
  inviteCode: {
    filter: inviteCodeInput, maxLength: 6, keyboardType: 'default', inputMode: 'text', ltr: true,
  },
  age: { filter: ageInput, maxLength: 2, keyboardType: 'number-pad', inputMode: 'numeric', ltr: true },
  personName: {
    filter: personNameInput, maxLength: 40, keyboardType: 'default', inputMode: 'text', ltr: false,
  },
  /** An ayah number: 286 is the longest surah, so three digits. */
  ayah: {
    filter: (v) => wholeNumberInput(v, 3), maxLength: 3,
    keyboardType: 'number-pad', inputMode: 'numeric', ltr: true,
  },
  /** A points threshold or a reward's cost. */
  count: {
    filter: (v) => wholeNumberInput(v, 6), maxLength: 6,
    keyboardType: 'number-pad', inputMode: 'numeric', ltr: true,
  },
};

/** Filters a value by the name of its format. */
export function applyFormat(name: FormatName, value: string): string {
  return FORMATS[name].filter(value);
}

/**
 * The attributes that make a web `<input>` declare its own format.
 *
 * Spread onto the element, so the field states what it accepts even before
 * any JavaScript runs: the ceiling is the browser's, the keypad is the
 * platform's, and `dir` keeps digits in reading order inside an RTL page.
 */
export function inputAttrs(name: FormatName): {
  maxLength: number;
  inputMode: 'tel' | 'numeric' | 'text';
  autoComplete?: string;
  dir?: 'ltr';
} {
  const f = FORMATS[name];
  return {
    maxLength: f.maxLength,
    inputMode: f.inputMode,
    ...(f.autoComplete ? { autoComplete: f.autoComplete } : {}),
    ...(f.ltr ? { dir: 'ltr' as const } : {}),
  };
}
